import React, { useEffect, useState } from 'react';
import cx from 'classnames';
import { isNull } from 'lodash';

import LoadingIndicator from '@magoosh/layouts/components/loading_indicator';
import { paths } from 'config/path_helpers';
import { secondsFormattedAsHoursAndMinutes, secondsFormattedAsMinutes } from 'utilities/time';

import * as api from '@test_prep/app/api';
import ContextualLink from '@test_prep/app/components/contextual_link';

import colors from '@magoosh/lib/styles/colors.module.scss';
import style from './style.module.scss';

interface Props {
  dataFetchKey: string;
  filters: any;
  practiceSession: {
    createdAt: number;
    name: string;
    promptCount: number;
    timeInSeconds: number;
  };
  studentId?: number;
}

export default (props: Props) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [summary, setSummary] = useState(null);

  const loadSummary = () => {
    setIsLoading(true);

    api.fetchAnswersSummary(props.filters, props.studentId).then((summary) => {
      setSummary(summary);
      setIsLoading(false);
    });
  };

  const paceColor = () => {
    if (summary.paceAverage < summary.otherPaceAverage) {
      return colors.brandPrimary;
    } else if (summary.paceAverage > summary.otherPaceAverage) {
      return colors.brandDanger;
    } else {
      return '#98A2B3';
    }
  };

  const scopedToPracticeSession = () => {
    return (props.filters['practice_session_id'] || props.filters['practice_session_ids']) && props.practiceSession
  };

  useEffect(() => {
    loadSummary();
  }, [props.filters, props.dataFetchKey]);

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center">
        <LoadingIndicator />
      </div>
    )
  }

  return (
    <div className="d-flex flex-column gap-m">
      <div className="align-items-sm-center d-flex flex-column flex-sm-row gap-s">
        <div className="small">
          {
            scopedToPracticeSession() ? (
              'Reviewing questions from a specific practice session'
            ) : (
              'Reviewing questions across all practice sessions and practice tests.'
            )
          }
        </div>

        <div className="d-flex flex-1 justify-content-sm-end">
          {
            // TODO - what filters should we persist through these links?
            scopedToPracticeSession() ? (
              <div className="d-flex flex-column flex-sm-row gap-xs">
                <ContextualLink href={paths.reviewAnswers(null, props.studentId)} style={{ color: '#424FBC' }}>
                  <strong>Review All Questions</strong>
                </ContextualLink>
               
                <div className="hidden-xs" style={{ color: '#424FBC' }}>|</div>
                
                <ContextualLink href={paths.reviewPracticeSessions(props.studentId)} style={{ color: '#424FBC' }}>
                  <strong>Review A Different Practice Session</strong>
                </ContextualLink>
              </div>
            ) : (
              <ContextualLink href={paths.reviewPracticeSessions(props.studentId)} style={{ color: '#424FBC' }}>
                <strong>Review A Specific Practice Session</strong>
              </ContextualLink>
            )
          }
        </div>
      </div>

      <div className={cx(style.divider, style.vertical)} />

      <div className="d-flex flex-column flex-sm-row gap-m">
        {
          summary.performance.map((performanceStat, idx) => (
            <React.Fragment key={idx}>
              <div className="align-items-center align-items-sm-start d-flex flex-1 flex-column gap-s">
                <div className="text-bold" style={{ color: '#697586' }}>
                  {
                    performanceStat.percentage ? (
                      `${props.studentId ? 'Student' : 'Your'} Performance`
                    ) : (
                      `${props.studentId ? 'Student' : 'Your'} Performance (Graded)`
                    )
                  }
                </div>

                <div className="align-items-center d-flex gap-xs">
                  <div className="h2 text-bold u-margin-A-n">
                    {
                      performanceStat.percentage ? (
                        `${performanceStat.value}%`
                      ) : (
                        isNull(performanceStat.value) ? 'NS' : performanceStat.value
                      )
                    }
                  </div>

                  <div style={{ color: '#697586' }}>
                    {
                      performanceStat.percentage ? (
                        `(${performanceStat.correctAnswerCount} of ${performanceStat.answerCount})`
                      ) : (
                        `(${performanceStat.answerCount || 0} questions)`
                      )
                    }
                  </div>
                </div>
              </div>

              {idx === 0 && summary.performance.length > 1 && <div className={style.divider} />}
            </React.Fragment>
          ))
        }

        <div className={style.divider} />

        <div className="align-items-center align-items-sm-start d-flex flex-1 flex-column gap-s">
          <div className="text-bold" style={{ color: '#697586' }}>
            {props.studentId ? 'Student': 'Your'} Average Pace
          </div>

          <div className="align-items-center d-flex gap-xs">
            <ClockIcon fill={paceColor()} />

            <div className="h2 text-bold u-margin-A-n" style={{ color: paceColor() }}>
              {secondsFormattedAsMinutes(summary.paceAverage, false)}
            </div>

            <div style={{ color: '#697586' }}>
              ({secondsFormattedAsHoursAndMinutes(summary.paceTotal)} total)
            </div>
          </div>
        </div>

        <div className={style.divider} />

        <div className="align-items-center align-items-sm-start d-flex flex-1 flex-column gap-s">
          <div className="text-bold" style={{ color: '#697586' }}>
            Others' Average Pace
          </div>

          <div className="align-items-center d-flex gap-xs">
            <ClockIcon fill="#98A2B3" />

            <div className="h2 text-bold u-margin-A-n">
              {secondsFormattedAsMinutes(summary.otherPaceAverage)}
            </div>

            <div style={{ color: '#697586' }}>
              ({secondsFormattedAsHoursAndMinutes(summary.otherPaceTotal)} total)
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ClockIcon = ({ fill }) => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" height={25} width={25} fill={fill}>
      <path d="M320 64c141.4 0 256 114.6 256 256S461.4 576 320 576 64 461.4 64 320 178.6 64 320 64m-24 120v136c0 8 4 15.5 10.7 20l96 64c11 7.4 25.9 4.4 33.3-6.7s4.4-25.9-6.7-33.3L344 307.2V184c0-13.3-10.7-24-24-24s-24 10.7-24 24"/>
    </svg>
  );
}
